import { useEffect, useState } from 'react';

import { fetchUserDetails } from '@/Utils/check-user-details';
import { updateServerAdmin } from '@/store/server-data-reducer/server-data-reducer';

import { useAppDispatch, useAppSelector } from './useRedux';

const adminCache: Record<string, { isAdmin: boolean; expires: number }> = {};

const CACHE_DURATION = 1000 * 60 * 30; // 30 minutes caching the result of each server

const useCheckAdmin = () => {
    const [isAdmin, setIsAdmin] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const dispatch = useAppDispatch();

    const activeServer = useAppSelector(
        state => state?.serverDataReducer?.activeServer,
    );

    const expressURL =
        useAppSelector(
            state =>
                state.serverDataReducer.serverData?.[activeServer]?.chat_server
                    ?.express_url,
        ) || undefined;

    useEffect(() => {
        if (!activeServer || !expressURL) return;

        const cached = adminCache[activeServer];
        const now = Date.now();

        // Use cached value if still valid
        if (cached && cached.expires > now) {
            setIsAdmin(cached.isAdmin);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        fetchUserDetails(expressURL, activeServer)
            .then(res => {
                const isAdminResult =
                    res?.user_details?.permissions?.['administrator'] || false;

                // Save in cache
                adminCache[activeServer] = {
                    isAdmin: isAdminResult,
                    expires: now + CACHE_DURATION,
                };

                setIsAdmin(isAdminResult);
            })
            .catch(err => console.error(err))
            .finally(() => setIsLoading(false));
    }, [expressURL, activeServer]);

    useEffect(() => {
        dispatch(updateServerAdmin(isAdmin));
    }, [isAdmin, dispatch]);

    return { isAdmin, isLoading, activeServer };
};

export default useCheckAdmin;
